import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, FileText, Building2, Wrench, Coins, ArrowRight, CheckCircle2, Package,
  Pen, Pickaxe, Blocks, Cone, Hammer, Drill, Power, Brush, Ruler, PaintBucket, Trees,
  Shield, Lock, RefreshCcw, Headphones, X, Sliders, Star, FileCheck, Clock, DollarSign,
  CreditCard
} from 'lucide-react';
import CreditPurchaseForm from '../../components/credits/CreditPurchaseForm';

const PurchaseCredits = () => {
  const navigate = useNavigate();

  // Function to handle successful purchase
  const handleSuccess = () => {
    navigate('/profile');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-primary mb-8">
        Acheter des crédits SimulReno
      </h1>

      {/* Introduction avec valeur */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Package className="h-5 w-5 text-primary mr-2" />
            Investissez dans vos travaux, pas dans les surprises
          </h2>
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-primary mb-2">Retour sur investissement incroyable</h3>
                <p className="text-gray-700">
                  Pour un projet de <span className="font-bold">10 000€</span>, une économie moyenne de <span className="font-bold">13%</span> représente <span className="font-bold text-green-600">1 300€ d'économies</span> pour un investissement de seulement 49,90€.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md text-center">
                <span className="text-sm text-gray-500">ROI moyen</span>
                <div className="text-3xl font-bold text-green-600">2506%</div>
              </div>
            </div>
          </div>
          <p className="text-gray-600">
            Chaque crédit vous donne accès à <span className="font-bold">une adresse à vie</span> avec la possibilité de créer des estimations détaillées illimitées pour cette même adresse. Comparez différentes options, mettez à jour vos projets sans frais supplémentaires et obtenez un document professionnel reconnu par les banques.
          </p>
        </div>
        
        {/* Compteur de confiance */}
        <div className="flex flex-col md:flex-row justify-center items-center py-4 border-t border-b border-gray-100 mb-6 gap-4">
          <div className="flex items-center">
            <Star className="h-5 w-5 text-yellow-500 mr-2" />
            <span className="text-sm text-gray-700">4.8/5 sur 293 avis clients</span>
          </div>
          <div className="hidden md:block mx-4 h-4 border-r border-gray-200"></div>
          <div className="flex items-center">
            <FileCheck className="h-5 w-5 text-blue-500 mr-2" />
            <span className="text-sm text-gray-700">4728+ estimations réalisées</span>
          </div>
          <div className="hidden md:block mx-4 h-4 border-r border-gray-200"></div>
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-sm text-gray-700">Validité à vie</span>
          </div>
        </div>
        
        {/* Section comparative */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Choisissez l'option qui vous convient :</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Option gratuite */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium">Estimation rapide</h4>
                <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">GRATUIT</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5 mr-2" />
                  <span>Vision globale du budget</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5 mr-2" />
                  <span>Estimation en 5 minutes</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-red-300 flex-shrink-0 mt-0.5 mr-2" />
                  <span>Sans détail poste par poste</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-red-300 flex-shrink-0 mt-0.5 mr-2" />
                  <span>Pas d'export PDF professionnel</span>
                </li>
                <li className="flex items-start text-gray-400">
                  <X className="h-5 w-5 text-red-300 flex-shrink-0 mt-0.5 mr-2" />
                  <span>Personnalisation limitée</span>
                </li>
              </ul>
              <button 
                onClick={() => navigate('/quick-estimate')}
                className="w-full py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Essayer gratuitement
              </button>
            </div>
            
            {/* Option payante */}
            <div className="bg-primary/5 border border-primary rounded-lg p-6 relative">
              <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">RECOMMANDÉ</span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-primary">Estimation détaillée</h4>
                <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">49,90€/ADRESSE</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 mr-2" />
                  <span><strong>1 crédit = 1 adresse à vie</strong> avec modifications illimitées</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 mr-2" />
                  <span>Détail complet poste par poste (250+ options)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 mr-2" />
                  <span>Export PDF pour dossier bancaire</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 mr-2" />
                  <span>13% d'économies en moyenne sur vos travaux</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 mr-2" />
                  <span>Idéal pour négocier avec les artisans</span>
                </li>
              </ul>
              <div className="text-sm text-gray-600 mb-4">
                Inclut la satisfaction garantie ou remboursé sous 14 jours
              </div>
            </div>
          </div>
        </div>
        
        {/* Témoignages ciblés */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Ils ont économisé grâce à l'estimation détaillée</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="flex justify-between mb-2">
                <span className="font-medium">Jean B., Lyon</span>
                <span className="text-green-600 font-semibold">-12 000€</span>
              </div>
              <p className="text-gray-600 text-sm italic">
                "Grâce au devis détaillé, j'ai économisé 12 000€ sur mes travaux de rénovation. Les artisans ont revu leurs prix quand je leur ai montré l'estimation détaillée de SimulReno."
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="flex justify-between mb-2">
                <span className="font-medium">Sophie M., Strasbourg</span>
                <span className="text-blue-600 font-semibold">Prêt obtenu : 45 000€</span>
              </div>
              <p className="text-gray-600 text-sm italic">
                "Mon prêt travaux a été accepté en une semaine ! La banque a apprécié le niveau de détail du devis SimulReno et sa crédibilité."
              </p>
            </div>
          </div>
        </div>
        
        {/* Visualisation concrète de ce que l'utilisateur va obtenir */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-6">Voici ce que vous obtenez avec un crédit</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-medium text-primary mb-3 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Un document professionnel détaillé
              </h4>
              <div className="bg-gray-100 p-4 rounded-lg mb-4 h-72 flex items-center justify-center">
                <img 
                  src="/detailed-result.png" 
                  alt="Exemple de document PDF généré" 
                  className="max-h-full object-contain"
                />
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-1 mr-2" />
                  <span className="text-sm">Détail des matériaux et main d'oeuvre</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-1 mr-2" />
                  <span className="text-sm">Présentation professionnelle pour votre banque</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-1 mr-2" />
                  <span className="text-sm">Format PDF prêt à l'emploi</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-primary mb-3 flex items-center">
                <Sliders className="h-5 w-5 mr-2" />
                Une configuration ultra-détaillée
              </h4>
              <div className="bg-gray-100 p-4 rounded-lg mb-4 h-72 flex items-center justify-center">
                <img 
                  src="/detailed-steps.png" 
                  alt="Interface de configuration détaillée" 
                  className="max-h-full object-contain"
                />
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-1 mr-2" />
                  <span className="text-sm">Interface intuitive par étapes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-1 mr-2" />
                  <span className="text-sm">Plus de 250 types de travaux configurables</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-1 mr-2" />
                  <span className="text-sm">Modifications illimitées à vie</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire d'achat */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h3 className="text-2xl font-bold text-center mb-4">Prêt à économiser sur vos travaux ?</h3>
        <p className="text-center text-lg mb-8">
          Pour seulement 49,90€, obtenez un outil professionnel qui a déjà fait économiser des milliers d'euros à nos clients.
        </p>

        <CreditPurchaseForm onSuccess={handleSuccess} />
        
      </div>

      {/* Avantages de l'estimation détaillée */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-6 text-primary">
          Une estimation ultra-détaillée pour des décisions éclairées
        </h2>

        {/* Étapes du processus */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Un processus complet où vous choisissez les travaux dont vous avez besoin :</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: <Pen className="h-5 w-5" />, text: "Études et diagnostics" },
              { icon: <Pickaxe className="h-5 w-5" />, text: "Démolition" },
              { icon: <Blocks className="h-5 w-5" />, text: "Gros œuvre" },
              { icon: <Cone className="h-5 w-5" />, text: "VRD (Voirie et Réseaux Divers)" },
              { icon: <Hammer className="h-5 w-5" />, text: "Isolation et étanchéité" },
              { icon: <Drill className="h-5 w-5" />, text: "Cloisons et aménagements" },
              { icon: <Power className="h-5 w-5" />, text: "Électricité et technique" },
              { icon: <Wrench className="h-5 w-5" />, text: "Plomberie et sanitaire" },
              { icon: <Brush className="h-5 w-5" />, text: "Revêtements" },
              { icon: <Ruler className="h-5 w-5" />, text: "Menuiseries" },
              { icon: <PaintBucket className="h-5 w-5" />, text: "Finitions" },
              { icon: <Trees className="h-5 w-5" />, text: "Extérieurs" },
              { icon: <FileText className="h-5 w-5" />, text: "Résultat détaillé" }
            ].map((step, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                {step.icon}
                <span>{step.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Points clés */}
        <div className="space-y-6 mb-8">
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-medium mb-1">Plus de 200 paramètres configurables</h4>
              <p className="text-gray-600">Personnalisez chaque aspect de votre rénovation avec une précision inégalée, des matériaux aux finitions.</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-medium mb-1">Un devis professionnel prêt à l'emploi</h4>
              <p className="text-gray-600">Générez instantanément un document détaillé parfait pour votre dossier bancaire ou vos négociations avec les artisans.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-medium mb-1">Protection contre les devis abusifs</h4>
              <p className="text-gray-600">Comparez les devis des artisans avec nos prix de référence pour chaque poste et évitez les mauvaises surprises.</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-medium mb-1">Estimations illimitées par adresse</h4>
              <p className="text-gray-600">Créez, modifiez et comparez autant de versions que vous le souhaitez pour une même adresse. Un crédit = une adresse à vie.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-6">Questions fréquentes sur les crédits</h2>
        <div className="space-y-4">
          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium">Comment fonctionnent les crédits exactement ?</summary>
            <div className="mt-3 text-gray-600">
              Un crédit SimulReno correspond à une adresse. Une fois acheté, vous pouvez créer autant d'estimations détaillées que vous souhaitez pour cette adresse, comparer différentes options, et mettre à jour votre projet sans limite de temps.
            </div>
          </details>
          
          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium">Quelle est la durée de validité des crédits ?</summary>
            <div className="mt-3 text-gray-600">
              Les crédits sont valables à vie pour l'adresse que vous avez sélectionnée. Il n'y a aucune date d'expiration.
            </div>
          </details>
          
          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium">Puis-je transférer un crédit d'une adresse à une autre ?</summary>
            <div className="mt-3 text-gray-600">
              Non, un crédit est lié à une adresse spécifique. Si vous avez plusieurs projets à différentes adresses, vous devrez acheter un crédit pour chaque adresse.
            </div>
          </details>
          
          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium">Comment fonctionne la garantie satisfait ou remboursé ?</summary>
            <div className="mt-3 text-gray-600">
              Si vous n'êtes pas satisfait de votre estimation détaillée, vous pouvez demander un remboursement intégral dans les 14 jours suivant votre achat. Il vous suffit de nous contacter via le formulaire de contact, sans condition.
            </div>
          </details>
          
          <details className="bg-gray-50 p-5 rounded-lg group cursor-pointer">
            <summary className="font-medium">Les banques acceptent-elles les estimations SimulReno ?</summary>
            <div className="mt-3 text-gray-600">
              Oui, nos estimations détaillées génèrent un document PDF professionnel qui est régulièrement accepté par les banques pour les demandes de prêt travaux. Le niveau de détail et la crédibilité de nos calculs sont particulièrement appréciés par les établissements financiers.
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCredits;